import { IonIcon, IonToggle } from '@ionic/react';
import { flashOutline } from 'ionicons/icons';
import React from 'react';
import {
  buildPlainEnglishSummary,
  formatRelativeTime,
} from '../../automation/workflowSummary';
import type { AutomationWorkflow } from '../../types/AutomationWorkflow.types';

const statusOf = (
  automation: AutomationWorkflow,
): { tone: 'idle' | 'live' | 'failed' | 'running'; label: string } => {
  const lastRun = automation.executionLog?.[0];
  if (lastRun?.status === 'running') {
    return { tone: 'running', label: 'Running' };
  }
  if (lastRun?.status === 'error') {
    return { tone: 'failed', label: 'Last run failed' };
  }
  if (automation.enabled) return { tone: 'live', label: 'Healthy' };
  return { tone: 'idle', label: 'Paused' };
};

export const AutomationCard: React.FC<{
  automation: AutomationWorkflow;
  onOpen: () => void;
  onToggleEnabled: (enabled: boolean) => void;
}> = ({ automation, onOpen, onToggleEnabled }) => {
  const status = statusOf(automation);
  const lastRun = formatRelativeTime(automation.lastRunAt);

  return (
    <article
      className={`automation-card status-${status.tone}`}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="automation-card-top">
        <span className="automation-card-icon-tile">
          <IonIcon icon={flashOutline} />
        </span>
        <div className="automation-card-copy">
          <h2>{automation.name}</h2>
          <p>
            {buildPlainEnglishSummary(
              automation.trigger ?? { type: 'manual', config: {} },
              automation.graph,
            )}
          </p>
        </div>
        <div
          className="automation-card-toggle"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          role="presentation"
        >
          <IonToggle
            checked={automation.enabled}
            aria-label={`Enable ${automation.name}`}
            onIonChange={(event) => onToggleEnabled(event.detail.checked)}
          />
        </div>
      </div>

      <footer className="automation-card-footer">
        <span className={`automation-card-status status-${status.tone}`}>
          <span className="automation-card-status-dot" aria-hidden />
          {status.label}
        </span>
        <span className="automation-card-meta-item">
          {automation.runCount} run{automation.runCount === 1 ? '' : 's'}
        </span>
        {lastRun && (
          <span className="automation-card-meta-item">{lastRun}</span>
        )}
        <span className="automation-card-footer-spacer" aria-hidden />
        <span className="automation-card-open">Open</span>
      </footer>
    </article>
  );
};
